import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  loadPaidEventPassProducts,
  migratePaidEventPassProductsIfEmpty,
  newAdminPaidEventProductId,
  type AdminPaidEventPassProduct,
} from '../../utils/adminPaidEventPassProducts';
import {
  deletePaidEventPassProductRemote,
  fetchPaidEventPassProductsAdmin,
  importPaidEventProductsFromLocalIfEmpty,
  insertPaidEventPassProduct,
  updatePaidEventPassProduct,
} from '../../services/platformPaidEventProductsService';
import {
  formatKoreanLongDate,
  formatYearMonthKo,
  planEndIsoFromStartMonthAndDurationMonths,
} from '../../utils/academicYearFebruaryEnd';
import { formatKrw } from '../../utils/subscriptionPaidPlanPricing';

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

type ProductFormFields = {
  eventName: string;
  flatAmountStr: string;
  startMonth: string;
  durationStr: string;
  activeUserStr: string;
};

function emptyFormFields(): ProductFormFields {
  return {
    eventName: '',
    flatAmountStr: '3000',
    startMonth: new Date().toISOString().slice(0, 7),
    durationStr: '13',
    activeUserStr: '10',
  };
}

function fieldsFromProduct(p: AdminPaidEventPassProduct): ProductFormFields {
  return {
    eventName: p.eventName,
    flatAmountStr: String(p.flatAmountWon),
    startMonth: p.paidPlanStartMonth,
    durationStr: String(p.durationMonths),
    activeUserStr: String(p.activeUserCount),
  };
}

function parseProductForm(
  fields: ProductFormFields,
):
  | {
      ok: true;
      eventName: string;
      flatAmountWon: number;
      paidPlanStartMonth: string;
      durationMonths: number;
      planEndIso: string;
      activeUserCount: number;
    }
  | { ok: false; error: string } {
  const eventName = fields.eventName.trim();
  if (!eventName) {
    return { ok: false, error: '유료 이벤트 이용권 이름을 입력해 주세요.' };
  }
  const rawA = Math.floor(Number(fields.flatAmountStr));
  if (!Number.isFinite(rawA) || rawA < 0) {
    return { ok: false, error: '고정 결제 금액을 올바르게 입력해 주세요.' };
  }
  const flatAmountWon = Math.min(9_999_999, rawA);
  const rawD = Math.floor(Number(fields.durationStr));
  const durationMonths = Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
  const paidPlanStartMonth = fields.startMonth.trim();
  const planEndIso = planEndIsoFromStartMonthAndDurationMonths(paidPlanStartMonth, durationMonths);
  if (!planEndIso) {
    return { ok: false, error: '이용 시작 월과 이용 기간을 확인해 주세요.' };
  }
  const rawU = Math.floor(Number(fields.activeUserStr));
  const activeUserCount = Math.min(999, Math.max(1, Number.isFinite(rawU) && rawU > 0 ? rawU : 1));
  return {
    ok: true,
    eventName,
    flatAmountWon,
    paidPlanStartMonth,
    durationMonths,
    planEndIso,
    activeUserCount,
  };
}

export interface AdminPaidEventPassRegisterSectionProps {
  workspaceId: string;
  /** 이전 학교 스코프 키가 있으면 비어 있는 대상 스코프로 한 번 이전 */
  legacyMigrateFromWorkspaceId?: string;
}

/**
 * 유료 이벤트 이용권 상품 등록(고정 금액).
 * EVT-/GRP- 코드 발급(AdminEventGroupPassIssuancePair)과 분리된 섹션.
 */
const AdminPaidEventPassRegisterSection: React.FC<AdminPaidEventPassRegisterSectionProps> = ({
  workspaceId,
  legacyMigrateFromWorkspaceId,
}) => {
  const [rows, setRows] = useState<AdminPaidEventPassProduct[]>([]);
  const [form, setForm] = useState<ProductFormFields>(() => emptyFormFields());
  const [formError, setFormError] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ProductFormFields>(() => emptyFormFields());
  const [editError, setEditError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (legacyMigrateFromWorkspaceId) {
      migratePaidEventPassProductsIfEmpty(workspaceId, legacyMigrateFromWorkspaceId);
    }
    try {
      const local = loadPaidEventPassProducts(workspaceId);
      let rows = await fetchPaidEventPassProductsAdmin();
      if (rows.length === 0 && local.length > 0) {
        rows = await importPaidEventProductsFromLocalIfEmpty(local);
      }
      setRows(rows);
      setLoadError(null);
    } catch (err) {
      console.error(err);
      setLoadError('유료 이벤트 이용권 목록을 DB에서 불러오지 못했습니다.');
      setRows(loadPaidEventPassProducts(workspaceId));
    }
  }, [workspaceId, legacyMigrateFromWorkspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!listOpen && !editingId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editingId) {
        setEditingId(null);
        setEditError(null);
        return;
      }
      setListOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [listOpen, editingId]);

  const durationMonthsPreview = useMemo(() => {
    const rawD = Math.floor(Number(form.durationStr));
    return Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
  }, [form.durationStr]);

  const previewPlanEndIso = useMemo(
    () => planEndIsoFromStartMonthAndDurationMonths(form.startMonth, durationMonthsPreview),
    [form.startMonth, durationMonthsPreview],
  );

  const editDurationPreview = useMemo(() => {
    const rawD = Math.floor(Number(editForm.durationStr));
    return Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
  }, [editForm.durationStr]);

  const editPreviewPlanEndIso = useMemo(
    () => planEndIsoFromStartMonthAndDurationMonths(editForm.startMonth, editDurationPreview),
    [editForm.startMonth, editDurationPreview],
  );

  const editingRow = useMemo(
    () => (editingId ? rows.find((r) => r.id === editingId) ?? null : null),
    [editingId, rows],
  );

  const onRegister = useCallback(async () => {
    const parsed = parseProductForm(form);
    if (!parsed.ok) {
      setFormError(parsed.error);
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      await insertPaidEventPassProduct({
        id: newAdminPaidEventProductId(),
        issuedAt: new Date().toISOString(),
        eventName: parsed.eventName,
        flatAmountWon: parsed.flatAmountWon,
        paidPlanStartMonth: parsed.paidPlanStartMonth,
        durationMonths: parsed.durationMonths,
        planEndIso: parsed.planEndIso,
        activeUserCount: parsed.activeUserCount,
        soldCount: 0,
        saleStatus: 'active',
      });
      setForm((prev) => ({ ...emptyFormFields(), startMonth: prev.startMonth }));
      await reload();
    } catch (err) {
      console.error(err);
      setFormError('등록을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }, [form, reload]);

  const openEdit = useCallback((row: AdminPaidEventPassProduct) => {
    setEditingId(row.id);
    setEditForm(fieldsFromProduct(row));
    setEditError(null);
  }, []);

  const closeEdit = useCallback(() => {
    setEditingId(null);
    setEditError(null);
  }, []);

  const onSaveEdit = useCallback(async () => {
    if (!editingId) return;
    const parsed = parseProductForm(editForm);
    if (!parsed.ok) {
      setEditError(parsed.error);
      return;
    }
    const current = rows.find((r) => r.id === editingId);
    if (!current) {
      setEditError('수정할 이용권을 찾을 수 없습니다.');
      return;
    }
    if (parsed.activeUserCount < current.soldCount) {
      setEditError(
        `이미 ${current.soldCount}명이 구매했습니다. 구매 가능 인원은 ${current.soldCount}명 이상으로 설정해 주세요.`,
      );
      return;
    }
    setEditError(null);
    setBusy(true);
    try {
      await updatePaidEventPassProduct(editingId, {
        eventName: parsed.eventName,
        flatAmountWon: parsed.flatAmountWon,
        paidPlanStartMonth: parsed.paidPlanStartMonth,
        durationMonths: parsed.durationMonths,
        planEndIso: parsed.planEndIso,
        activeUserCount: parsed.activeUserCount,
      });
      await reload();
      closeEdit();
    } catch (err) {
      console.error(err);
      setEditError('수정을 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [editingId, editForm, rows, reload, closeEdit]);

  const onClose = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        await updatePaidEventPassProduct(id, { saleStatus: 'closed' });
        await reload();
      } catch (err) {
        console.error(err);
        window.alert('판매 마감 처리에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const onResume = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        await updatePaidEventPassProduct(id, { saleStatus: 'active' });
        await reload();
      } catch (err) {
        console.error(err);
        window.alert('판매 재개 처리에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const onDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('이 유료 이벤트 이용권 등록을 제거할까요? 되돌릴 수 없습니다.')) return;
      setBusy(true);
      try {
        await deletePaidEventPassProductRemote(id);
        if (editingId === id) closeEdit();
        await reload();
      } catch (err) {
        console.error(err);
        window.alert('삭제에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reload, editingId, closeEdit],
  );

  const renderFormFields = (
    fields: ProductFormFields,
    setFields: React.Dispatch<React.SetStateAction<ProductFormFields>>,
    idPrefix: string,
  ) => (
    <>
      <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--name">
        <label className="cp-admin-users-pass-issue-label" htmlFor={`${idPrefix}-name`}>
          이용권 이름
        </label>
        <input
          id={`${idPrefix}-name`}
          className="cp-admin-users-pass-issue-input"
          type="text"
          maxLength={200}
          placeholder="예: 2026학년도 유료 이벤트 이용권"
          value={fields.eventName}
          onChange={(e) => setFields((prev) => ({ ...prev, eventName: e.target.value }))}
          autoComplete="off"
        />
      </div>
      <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--secondary">
        <label className="cp-admin-users-pass-issue-label" htmlFor={`${idPrefix}-amount`}>
          고정 결제 금액(원)
        </label>
        <input
          id={`${idPrefix}-amount`}
          className="cp-admin-users-pass-issue-input"
          type="number"
          inputMode="numeric"
          min={0}
          max={9999999}
          value={fields.flatAmountStr}
          onChange={(e) => setFields((prev) => ({ ...prev, flatAmountStr: e.target.value }))}
        />
      </div>
      <div className="cp-admin-users-pass-issue-pair-fields cp-admin-users-pass-issue-pair-fields--plan-band cp-admin-pass-slot cp-admin-pass-slot--plan">
        <div className="cp-admin-users-pass-issue-field">
          <label className="cp-admin-users-pass-issue-label" htmlFor={`${idPrefix}-start`}>
            이용 시작 월
          </label>
          <input
            id={`${idPrefix}-start`}
            className="cp-admin-users-pass-issue-input"
            type="month"
            value={fields.startMonth}
            onChange={(e) => setFields((prev) => ({ ...prev, startMonth: e.target.value }))}
          />
        </div>
        <div className="cp-admin-users-pass-issue-field">
          <label className="cp-admin-users-pass-issue-label" htmlFor={`${idPrefix}-duration`}>
            이용 기간(개월)
          </label>
          <input
            id={`${idPrefix}-duration`}
            className="cp-admin-users-pass-issue-input"
            type="number"
            inputMode="numeric"
            min={1}
            max={36}
            value={fields.durationStr}
            onChange={(e) => setFields((prev) => ({ ...prev, durationStr: e.target.value }))}
          />
        </div>
      </div>
      <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--users">
        <label className="cp-admin-users-pass-issue-label" htmlFor={`${idPrefix}-users`}>
          구매 가능 인원(슬롯)
        </label>
        <input
          id={`${idPrefix}-users`}
          className="cp-admin-users-pass-issue-input"
          type="number"
          inputMode="numeric"
          min={1}
          max={999}
          value={fields.activeUserStr}
          onChange={(e) => setFields((prev) => ({ ...prev, activeUserStr: e.target.value }))}
        />
      </div>
    </>
  );

  return (
    <article
      className="cp-dashboard-block cp-admin-users-pass-block cp-admin-users-pass-issue-pair-col"
      aria-labelledby="cp-admin-paid-event-product-head"
    >
        <h3 className="cp-dashboard-block__head" id="cp-admin-paid-event-product-head">
          <span className="cp-dashboard-block__head-title">유료 이벤트 이용권 카드 등록</span>
        </h3>
        <div className="cp-admin-users-pass-issue-pair-intro">
          {loadError ? (
            <p className="cp-admin-users-pass-issue-form-error" role="alert">
              {loadError}
            </p>
          ) : null}
        </div>
        <div className="cp-dashboard-block__body cp-admin-users-pass-block-body">
          <div className="cp-admin-users-pass-issue-form cp-admin-users-pass-issue-form--stack">
            {renderFormFields(form, setForm, 'cp-admin-paid-evt')}
            <div className="cp-admin-users-pass-issuance-preview-band cp-admin-pass-slot cp-admin-pass-slot--preview">
              {previewPlanEndIso && /^\d{4}-\d{2}$/.test(form.startMonth.trim()) ? (
                <p className="cp-admin-users-pass-issue-fixed-end">
                  이용권 이용일:{' '}
                  <strong>{formatKoreanLongDate(`${form.startMonth.trim()}-01`)}</strong>
                  {' ~ '}
                  <strong>{formatKoreanLongDate(previewPlanEndIso)}</strong>
                </p>
              ) : (
                <p className="cp-admin-users-pass-issuance-preview-placeholder">
                  시작 월과 이용 기간을 입력하면 이용일이 표시됩니다.
                </p>
              )}
              {formError ? (
                <p className="cp-admin-users-pass-issue-form-error" role="alert">
                  {formError}
                </p>
              ) : null}
            </div>
            <div className="cp-admin-users-pass-issue-actions cp-admin-users-pass-issue-actions--block cp-admin-pass-slot cp-admin-pass-slot--actions">
              <button
                type="button"
                className="cp-btn-primary cp-admin-users-pass-issue-create-btn"
                onClick={() => void onRegister()}
                disabled={busy}
              >
                유료 이벤트 이용권 카드 등록
              </button>
            </div>
          </div>
          <div className="cp-admin-users-pass-issue-list-open cp-admin-pass-slot cp-admin-pass-slot--list">
            <button
              type="button"
              className="cp-btn-secondary cp-admin-users-pass-issue-list-open-btn"
              onClick={() => setListOpen(true)}
            >
              등록된 유료 이벤트 이용권
              {rows.length > 0 ? ` (${rows.length})` : ''}
            </button>
          </div>
        </div>

      {listOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onClick={() => {
                if (editingId) return;
                setListOpen(false);
              }}
            >
          <div
            className="cp-modal cp-admin-users-pass-detail-modal cp-admin-users-pass-issuance-list-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-paid-evt-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-paid-evt-list-title">등록된 유료 이벤트 이용권</h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              {rows.length === 0 ? (
                <p className="cp-admin-users-pass-modal-empty" role="status">
                  아직 등록된 유료 이벤트 이용권이 없습니다.
                </p>
              ) : (
                <div className="cp-admin-users-pass-issue-table-wrap" tabIndex={0}>
                  <table className="cp-admin-users-pass-issue-table cp-admin-users-pass-issue-table--modal">
                    <thead>
                      <tr>
                        <th scope="col">등록일시</th>
                        <th scope="col">이름</th>
                        <th scope="col">금액</th>
                        <th scope="col">시작월</th>
                        <th scope="col">기간(월)</th>
                        <th scope="col">이용권 종료</th>
                        <th scope="col">구매 한도</th>
                        <th scope="col">상태</th>
                        <th scope="col">수정</th>
                        <th scope="col">마감·재개</th>
                        <th scope="col">제거</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>{formatTs(r.issuedAt)}</td>
                          <td className="cp-admin-users-pass-issue-memo">{r.eventName}</td>
                          <td className="cp-admin-users-pass-issue-num">
                            {formatKrw(r.flatAmountWon)}원
                          </td>
                          <td>{formatYearMonthKo(r.paidPlanStartMonth)}</td>
                          <td className="cp-admin-users-pass-issue-num">{r.durationMonths}</td>
                          <td>{formatKoreanLongDate(r.planEndIso)}</td>
                          <td className="cp-admin-users-pass-issue-num">
                            {r.soldCount}/{r.activeUserCount}
                          </td>
                          <td>
                            <span
                              className={
                                'cp-admin-users-pass-issue-status-pill' +
                                (r.saleStatus === 'closed'
                                  ? ' cp-admin-users-pass-issue-status-pill--muted'
                                  : ' cp-admin-users-pass-issue-status-pill--ok')
                              }
                            >
                              {r.saleStatus === 'closed'
                                ? '마감'
                                : r.soldCount >= r.activeUserCount
                                  ? '매진'
                                  : '활성'}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                              onClick={() => openEdit(r)}
                            >
                              수정
                            </button>
                          </td>
                          <td>
                            <div className="cp-admin-users-pass-issue-table-actions">
                              {r.saleStatus === 'active' ? (
                                <button
                                  type="button"
                                  className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                                  onClick={() => onClose(r.id)}
                                >
                                  마감
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                                  onClick={() => onResume(r.id)}
                                >
                                  재개
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn cp-admin-users-pass-issue-table-action-btn--danger"
                              onClick={() => onDelete(r.id)}
                            >
                              제거
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setListOpen(false)}
                disabled={!!editingId}
              >
                닫기
              </button>
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}

      {editingId && editingRow && typeof document !== 'undefined'
        ? createPortal(
            <div className="cp-modal-backdrop" role="presentation" onClick={closeEdit}>
          <div
            className="cp-modal cp-admin-users-pass-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-paid-evt-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-paid-evt-edit-title">유료 이벤트 이용권 카드 수정</h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              <div className="cp-admin-users-pass-issue-form cp-admin-users-pass-issue-form--stack">
                {renderFormFields(editForm, setEditForm, 'cp-admin-paid-evt-edit')}
                <div className="cp-admin-users-pass-issuance-preview-band">
                  {editPreviewPlanEndIso && /^\d{4}-\d{2}$/.test(editForm.startMonth.trim()) ? (
                    <p className="cp-admin-users-pass-issue-fixed-end">
                      이용권 이용일:{' '}
                      <strong>{formatKoreanLongDate(`${editForm.startMonth.trim()}-01`)}</strong>
                      {' ~ '}
                      <strong>{formatKoreanLongDate(editPreviewPlanEndIso)}</strong>
                    </p>
                  ) : (
                    <p className="cp-admin-users-pass-issuance-preview-placeholder">
                      시작 월과 이용 기간을 입력하면 이용일이 표시됩니다.
                    </p>
                  )}
                </div>
                {editError ? (
                  <p className="cp-admin-users-pass-issue-form-error" role="alert">
                    {editError}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeEdit}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={onSaveEdit}>
                저장
              </button>
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}
    </article>
  );
};

export default AdminPaidEventPassRegisterSection;
